import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

// Third Party Libraries
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Hooks
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  CLEAR_ADMIN_BATCHES_FILTERS,
  SET_ADMIN_BATCHES_FILTERS,
} from "~/redux/actions/action-types.js";
import { failure } from "~/redux/actions/snackbar-actions";

// Utils
import { MESSAGES } from "~/utils/message.js";
import {
  dateProtector,
  handleRawChangeForDatePicker,
} from "~/utils/date-format.js";

// Components
import MultiSelect from "~/components/General/MultiSelect";

// Mui components
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Box, Grid, InputLabel } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// images
import { ModalCrossCIcon } from "~/assets/images";

// Styles
import "./styles.scss";


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [selectedAccuZip, setSelectedAccuZip] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [createdDate, setCreatedDate] = useState([null, null]);
  const [startDate, endDate] = createdDate;

  const dispatch = useDispatch();

  const accuzipStatus = useSelector(
    (state) => state.adminBatchesReducers.filterslabels.accuZipStatus
  );

  const batchStatus = useSelector(
    (state) => state.adminBatchesReducers.filterslabels.batchStatus
  );

  const productType = useSelector(
    (state) => state.adminBatchesReducers.filterslabels.productType
  );

  const perPage = useSelector(
    (state) => state.adminBatchesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const filters = useSelector((state) => state.adminBatchesReducers.filters);

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedAccuZip([]);
    setSelectedBatch([]);
    setSelectedProduct([]);
    setCreatedDate([null, null]);
    !persist ? dispatch({ type: CLEAR_ADMIN_BATCHES_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    const dateRange =
      startDate && endDate
        ? {
            startDate: startDate.toISOString().slice(0, 10),
            endDate: endDate.toISOString().slice(0, 10),
          }
        : null;

    if (startDate && endDate === null) {
      dispatch(failure(MESSAGES.CONTACTS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        accuzipStatus: selectedAccuZip,
        batchStatus: selectedBatch,
        productType: selectedProduct,
        createdAt: dateRange,
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      const value = payload.filters[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ADMIN_BATCHES_FILTERS, payload: payload });
    onClose();
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (Object.values(filters).length) {
      setSelectedAccuZip(filters.accuzipStatus);
      setSelectedBatch(filters.batchStatus);
      setSelectedProduct(filters.productType);
      filters.createdAt
        ? setCreatedDate([
            new Date(filters?.createdAt?.startDate),
            new Date(filters?.createdAt?.endDate),
          ])
        : undefined;
    }
  }, [open]);

  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      className="batchfilterModalWrapper"
    >
      <DialogTitle
        className="filterModalTitle"
        sx={{ m: 0, p: 2 }}
        id="customized-dialog-title"
      >
        {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.HEADING}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 5,
          top: 5,
          padding: "0",
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <ModalCrossCIcon />
      </IconButton>
      <DialogContent className="filterModalContent">
        <Grid container spacing={2}>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="inputWrapper">
              <Typography>
                {
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .ACCUZIP_STATUS_LABEL
                }
              </Typography>
              <MultiSelect
                options={accuzipStatus}
                selectedValue={selectedAccuZip}
                setSelectedValue={setSelectedAccuZip}
                placeHolderText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .ACCUZIP_STATUS_PLACEHOLDER
                }
                noOptionText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .NO_ACCUZIP_STATUS
                }
              />
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="inputWrapper">
              <Typography>
                {
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .BATCH_STATUS_LABEL
                }
              </Typography>
              <MultiSelect
                options={batchStatus}
                selectedValue={selectedBatch}
                setSelectedValue={setSelectedBatch}
                placeHolderText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .BATCH_STATUS_PLACEHOLDER
                }
                noOptionText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .NO_BATCH_STATUS
                }
              />
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate">
              <Box className="selectDate">
                <InputLabel className="dateLabel">
                  {
                    MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                      .CREATED_DATE_LABEL
                  }
                </InputLabel>
                <KeyboardArrowDownIcon
                  sx={{
                    color: isDatepickerOpen ? "#ED5C2F" : "inherit",
                  }}
                />
                <DatePicker
                  onCalendarClose={() => setIsDatepickerOpen(false)}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    dateProtector(update);
                    setCreatedDate(update);
                  }}
                  minDate={null}
                  onChangeRaw={(e) =>
                    handleRawChangeForDatePicker(e, setCreatedDate)
                  }
                  onFocus={() => setIsDatepickerOpen(true)}
                  onBlur={(e) => {
                    if (e.target.value == "") {
                      setCreatedDate([null, null]);
                    }
                    setIsDatepickerOpen(false);
                  }}
                  placeholderText={
                    MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                      .DATE_PLACEHOLDER
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="inputWrapper">
              <Typography>
                {
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .PRODUCT_TYPE_LABEL
                }
              </Typography>
              <MultiSelect
                options={productType}
                selectedValue={selectedProduct}
                setSelectedValue={setSelectedProduct}
                placeHolderText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .PRODUCT_TYPE_PLACEHOLDER
                }
                noOptionText={
                  MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS
                    .NO_PRODUCT_TYPE
                }
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="actionBtns">
        <Button onClick={() => clearFilters()}>
          {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.CANCEL_BUTTON}
        </Button>
        <Button onClick={applyFilters}>
          {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.SUBMIT_BUTTON}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AdvanceFilters;
